import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, X, User, Car, CreditCard, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Audio context for admin alert sounds
let audioCtx = null;
let audioUnlocked = false;
let alarmAudio = null;

const unlockAudio = () => {
  if (audioUnlocked) return;
  try {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    // Pre-load alarm audio WITHOUT playing it (no beep)
    if (!alarmAudio) {
      alarmAudio = new Audio('/alarm.wav');
      alarmAudio.volume = 1.0;
      alarmAudio.load();
    }
    audioUnlocked = true;
  } catch (e) {}
};

// Do NOT register global listeners at module level — only inside the component

const playAlertSound = () => {
  try {
    // Vibration
    if (navigator.vibrate) {
      navigator.vibrate([300, 100, 300, 100, 400]);
    }
    
    // Method 1: HTML Audio (louder, works after unlock on iOS)
    if (alarmAudio) {
      alarmAudio.currentTime = 0;
      alarmAudio.play().catch(() => {});
    }
    
    // Method 2: Web Audio API oscillator
    if (audioCtx) {
      if (audioCtx.state === 'suspended') audioCtx.resume();
      const now = audioCtx.currentTime;
      for (let i = 0; i < 4; i++) {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.value = 880 + (i * 220);
        osc.type = 'square';
        gain.gain.setValueAtTime(0.7, now + i * 0.25);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.25 + 0.2);
        osc.start(now + i * 0.25);
        osc.stop(now + i * 0.25 + 0.2);
      }
    }
  } catch (e) {
    console.log('Audio alert failed:', e);
  }
};

const getNotificationIcon = (type) => {
  switch (type) {
    case 'new_booking':
    case 'new_quick_booking':
      return <Car className="w-4 h-4 text-emerald-400" />;
    case 'payment_received':
      return <CreditCard className="w-4 h-4 text-blue-400" />;
    case 'new_registration':
      return <User className="w-4 h-4 text-purple-400" />;
    default:
      return <Bell className="w-4 h-4 text-zinc-400" />;
  }
};

const getNotificationColor = (type) => {
  switch (type) {
    case 'new_booking':
    case 'new_quick_booking':
      return 'border-emerald-500/30 bg-emerald-500/10';
    case 'payment_received':
      return 'border-blue-500/30 bg-blue-500/10';
    case 'new_registration':
      return 'border-purple-500/30 bg-purple-500/10';
    default:
      return 'border-zinc-700 bg-zinc-800';
  }
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  return `Il y a ${diffDays}j`;
};

export const AdminNotifications = ({ adminToken }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const previousUnreadCount = useRef(null);
  const dropdownRef = useRef(null);

  // Register audio unlock listeners ONLY when admin component mounts
  useEffect(() => {
    const events = ['touchstart', 'touchend', 'click', 'keydown'];
    events.forEach(evt => {
      document.addEventListener(evt, unlockAudio, { passive: true });
    });
    return () => {
      events.forEach(evt => {
        document.removeEventListener(evt, unlockAudio);
      });
    };
  }, []);

  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${adminToken}`
  }), [adminToken]);

  const playNotificationSound = useCallback(() => {
    playAlertSound();
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!adminToken) return;
    
    try {
      const response = await axios.get(`${API}/admin/notifications`, {
        headers: getAuthHeader(),
        params: { limit: 20 }
      });
      
      setNotifications(response.data.notifications);
      const newUnreadCount = response.data.unread_count;
      
      // Play sound if new notifications arrived (skip first fetch to avoid sound on page load)
      if (previousUnreadCount.current !== null && newUnreadCount > previousUnreadCount.current) {
        playNotificationSound();
        
        // Show browser notification if permission granted
        if (Notification.permission === 'granted') {
          const latestNotif = response.data.notifications.find(n => !n.read);
          if (latestNotif) {
            new Notification('YOUMI GREEN VTC', {
              body: `${latestNotif.title}: ${latestNotif.message}`,
              icon: '/favicon.ico',
              tag: latestNotif.id
            });
          }
        }
      }
      
      previousUnreadCount.current = newUnreadCount;
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [adminToken, getAuthHeader, playNotificationSound]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/admin/notifications/${notificationId}/read`, null, {
        headers: getAuthHeader()
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/admin/notifications/read-all`, null, {
        headers: getAuthHeader()
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/admin/notifications/${notificationId}`, {
        headers: getAuthHeader()
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification supprimée');
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Fetch notifications on mount and poll every 15 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-zinc-800 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-emerald-500' : 'text-zinc-400'}`} />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in-up">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 bg-zinc-800/50">
            <h3 className="font-bold text-white font-['Outfit']">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                Tout marquer lu
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucune notification</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-4 border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors ${
                    !notification.read ? getNotificationColor(notification.type) : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`font-medium text-sm ${!notification.read ? 'text-white' : 'text-zinc-400'}`}>
                          {notification.title}
                        </p>
                        <span className="text-xs text-zinc-500 whitespace-nowrap">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400 mt-0.5 truncate">
                        {notification.message}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-1 hover:bg-zinc-700 rounded text-zinc-500 hover:text-emerald-400"
                          title="Marquer comme lu"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-1 hover:bg-zinc-700 rounded text-zinc-500 hover:text-red-400"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-zinc-700 bg-zinc-800/30">
              <p className="text-xs text-zinc-500 text-center">
                Actualisation automatique toutes les 15s
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
